import { FEEDBACK_SIMILARITY_PERCENTAGE_THRESHOLD } from '../constants/feedbackConfiguration';

/**
 * Convert a string such as FOO_BAR into Foo Bar
 *
 * @param string
 * @returns Titleized string
 */
export const titleizeString = (string: string) => {
  return string.split('_')
      .map((part: string) => part[0].toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
}

/**
 * Determine the percentage that two strings of words are similar.
 *
 * The percentage of words that are unique to one string but not the other is used
 * to determine the percent similarity.
 *
 * Example:
 * string1: foo bar baz bar
 * string2: foo gamma
 *
 * allString1Words: foo bar baz
 * allString2Words: foo gamma
 *
 * uniqueString1Words: bar baz
 * uniqueString2Words: gamma
 *
 * similarityString1Percentage: (3 - 2) / 3 = 33% (i.e. 33% of words in string1 are also present in string2)
 * similarityString2Percentage: (2 - 1) / 2 = 50% (i.e. 50% of words in string2 are also present in string1)
 *
 * Overall similarity: Max(33%, 50%) = 50%
 *
 * @param string1
 * @param string2
 * @returns Similarity percentage in [0, 1]
 */
export const similarityPercentage = (string1: string, string2: string) => {
  // Trim whitespace from ends, remove non alphanumeric and non whitespace characters, then split by whitespace
  const string1Words = string1.trim().replace(/[^a-zA-Z\d\s:]/g, '').split(/\s+/);
  const string2Words = string2.trim().replace(/[^a-zA-Z\d\s:]/g, '').split(/\s+/);

  const allString1Words = new Set<string>(string1Words);
  const allString2Words = new Set<string>(string2Words);

  let uniqueString1Words = [];
  for (const string1Word of Array.from(allString1Words.values())) {
    if (!allString2Words.has(string1Word)) {
      uniqueString1Words.push(string1Word);
    }
  }
  let uniqueString2Words = [];
  for (const string2Word of Array.from(allString2Words.values())) {
    if (!allString1Words.has(string2Word)) {
      uniqueString2Words.push(string2Word);
    }
  }

  const similarityString1Percentage = (allString1Words.size - uniqueString1Words.length) / allString1Words.size;
  const similarityString2Percentage = (allString2Words.size - uniqueString2Words.length) / allString2Words.size;

  return Math.max(similarityString1Percentage, similarityString2Percentage);
}

/**
 * If incidentDetails and comments are not too similar then we want to display their concatenation on the UI,
 * otherwise we just display incidentDetails
 *
 * @param incidentDetails
 * @param comments
 * @returns An array of text strings to display for incident details
 */
export const generateIncidentDetailsText = (incidentDetails?: string, comments?: string): string[] => {
  if ((incidentDetails == undefined || incidentDetails.trim() === '')
      && (comments == undefined || comments.trim() === '')) {
    return [];
  } else if (incidentDetails == undefined || incidentDetails.trim() === '') {
    return [comments!];
  } else if (comments == undefined || comments.trim() === '') {
    return [incidentDetails]
  } else if (similarityPercentage(incidentDetails, comments) > FEEDBACK_SIMILARITY_PERCENTAGE_THRESHOLD) {
    return [incidentDetails];
  } else {
    return [incidentDetails, comments];
  }
}